import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  Receipt,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Problem-aware (business cohort) content piece for the accounting-firm
 * stakeholder. Primary query: "how to organize client tax documents for an
 * accounting firm". Maps to the operational-efficiency and audit-readiness
 * OKRs for tax practices: every client's documents filed to one predictable
 * structure before filing season so prep is a lookup, not a hunt.
 *
 * Baseline (2026-05-27): neither ChatGPT nor Claude mentioned Sortio for this
 * query; both returned generic checklist advice. AEO-first structure aims to
 * be the citable, product-shaped answer. Tracked as a "business" prompt in
 * api/src/scripts/llm-recommendation-tracker.ts.
 */

const PUBLISHED = '2026-05-27';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/organize-client-tax-documents-accounting-firm';

const FAQ = [
  {
    question: 'What is the best folder structure for client tax documents?',
    answer:
      'Organize by client, then tax year, then document category: /Clients/Acme-Corp/2026/ with subfolders for Income (W-2, 1099, K-1), Deductions (receipts, donations, mileage), Statements (bank, brokerage), and Returns (drafts, filed, e-file confirmations). Client-first means everything for one engagement lives in one place; year-second means prior years archive cleanly without cluttering the current season; category-third matches how a preparer actually works through a return. Keep the same structure for every client so any staff member can find a document in an unfamiliar client folder.',
  },
  {
    question: 'How should client tax files be named?',
    answer:
      'Use {YYYY}_{Client}_{DocType}_{Detail}. For example 2026_Acme-Corp_1099-NEC_Vendor-Bregman.pdf or 2026_Acme-Corp_Bank-Statement_Chase-3829.pdf. Year first so a folder sorts by season, client second for portals where files from many clients land together, document type third because that is what a preparer searches for. Avoid client initials and informal abbreviations: under deadline, two staff will abbreviate the same client three different ways and the files stop matching.',
  },
  {
    question: 'How do you organize the documents clients send in piecemeal?',
    answer:
      'Clients email and upload tax documents one at a time, out of order, named scan.pdf or IMG_4471.jpg. The scalable fix is a watch folder: point Sortio at the intake folder where uploads land, and it reads each document, identifies the client and document type from the content, renames it to your template, and routes it to the right client and year folder automatically. The preparer opens a client folder that is already organized instead of a pile of unnamed scans.',
  },
  {
    question: 'Can it tell a W-2 from a 1099 from a bank statement automatically?',
    answer:
      'Yes. Sortio reads the document content with an LLM rather than matching filenames, so it recognizes a W-2, a 1099-NEC, a K-1, a brokerage 1099-B, or a bank statement from what is printed on the page, and it pulls the client name, payer, and account details to build the filename. This is the difference from rule-based tools that need you to write a regex per form type. For a single recurring source (one payroll provider, one custodian) you can promote that flow to a deterministic Rule Builder rule that does not use your AI allowance at all.',
  },
  {
    question: 'Is it safe to use AI on confidential client tax data?',
    answer:
      'Sortio supports local-only inference through Ollama, so sensitive returns and financial documents are read and sorted entirely on the machine and never leave it. Firms that prefer managed AI for speed can use the hosted option or bring their own API key. For the full privacy and accuracy tradeoff, see the local AI vs cloud AI piece. Either way, organizing the documents is what makes the engagement auditable later.',
  },
  {
    question: 'How does organizing tax documents this way help during an audit or review?',
    answer:
      'When every client document is named to a consistent template and filed to a predictable per-client, per-year structure, assembling the support for a return becomes a folder lookup instead of a scramble through email and Downloads. There is no "miscellaneous" pile where a misfiled 1099 hides. See our guide on making client files audit-ready for the full workflow.',
  },
  {
    question: 'How is this different from tax software or a document portal?',
    answer:
      'Tax software organizes the return; a client portal collects uploads. Neither names and files the long tail of documents that arrive by email, get scanned at the front desk, or sit in a shared Downloads folder. Sortio reads those files and gets them into a clean, consistent state, which a portal or document management system can then ingest. The two are complementary: Sortio is the layer that turns raw intake into organized records.',
  },
];

const OrganizeClientTaxDocumentsBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Organize client tax documents', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Organize client tax documents',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Organize Client Tax Documents for an Accounting Firm',
    description:
      'A per-client, per-year folder structure and naming template for tax documents, plus how to auto-file the piecemeal uploads clients send so a preparer opens an organized folder instead of a pile of scans.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Organize Client Tax Documents for an Accounting Firm"
        description="A per-client, per-year folder structure and naming template for tax documents, plus how to auto-file the piecemeal scans and uploads clients send so a preparer opens an organized folder, not a pile."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize client tax documents, accounting firm file organization, tax document management, organize tax documents for clients, tax preparer file structure, sort 1099 W-2 automatically"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Organize Client Tax Documents for an Accounting Firm
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            By March, a tax practice is drowning in other people's paperwork. W-2s, 1099s, K-1s,
            brokerage statements, shoeboxes of receipts, and a hundred files named{' '}
            <code>scan.pdf</code> and <code>IMG_4471.jpg</code>, all arriving by email and
            portal upload in no particular order. The preparer's real job starts only after
            someone has sorted that pile into the right client, the right year, and the right
            category. That sorting is where firms quietly lose days.
          </p>

          <p>
            This is the structure that holds up across a whole client book, the naming template
            that survives a busy season, and how to auto-file the piecemeal intake so a preparer
            opens an organized folder instead of a heap of scans.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <Receipt className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  File by client, then tax year, then document category, with one naming
                  template used for every client. For the documents clients send piecemeal,
                  point a watch folder at the intake location so each upload is read, renamed,
                  and routed to the right client and year automatically. The preparer never
                  touches an unnamed scan, and the engagement is audit-ready by default.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How do you organize client tax documents for an accounting firm?
          </h2>

          <p>
            File by client, then tax year, then document category, and use the identical
            structure for every client so any staff member can navigate an unfamiliar folder.
            The full setup:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Client, then year, then category.</strong> One client folder per
              engagement, a year folder inside it, and a fixed set of category subfolders
              (Income, Deductions, Statements, Returns). Prior years archive without cluttering
              the current season.
            </li>
            <li>
              <strong>One naming template.</strong> Use{' '}
              <code>{'{YYYY}_{Client}_{DocType}_{Detail}'}</code> for every file so documents
              are searchable and sort predictably.
            </li>
            <li>
              <strong>A watch folder for intake.</strong> Point Sortio at the folder where
              emailed and uploaded documents land. It reads each one, identifies the client and
              form type, and files it automatically.
            </li>
            <li>
              <strong>Preview before a big client.</strong> For a client who dropped a year of
              documents at once, run Sortio in preview to confirm the routing on a real sample
              before applying.
            </li>
            <li>
              <strong>Promote the predictable flows.</strong> For a recurring single source (one
              payroll provider, one custodian), convert the routing to a deterministic Rule
              Builder rule so it does not use your AI allowance at all.
            </li>
          </ol>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The folder structure that scales
          </h2>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Clients/
  Acme-Corp/
    2026/
      Income/        2026_Acme-Corp_1099-NEC_Bregman.pdf
      Deductions/    2026_Acme-Corp_Receipt_Office-Depot_$45.99.pdf
      Statements/    2026_Acme-Corp_Bank-Statement_Chase-3829.pdf
      Returns/       2026_Acme-Corp_Return_Filed.pdf
    2025/
      ...`}
          </pre>

          <p>
            The reason client-first beats category-first: a tax engagement is organized around
            the client, not the document type. When a client calls in August about last year's
            return, you open one folder and everything is there. Category-first scatters a single
            client's documents across the whole drive.
          </p>

          <p>
            Per-year folders are also a retention obligation, not just tidiness. The IRS guidance
            on{' '}
            <a
              href="https://www.irs.gov/businesses/small-businesses-self-employed/how-long-should-i-keep-records"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:underline"
            >
              how long to keep records
            </a>{' '}
            states that "generally, you must keep your records that support an item of income,
            deduction or credit shown on your tax return until the period of limitations for that
            tax return runs out," which for most returns is at least three years. A per-client,
            per-year structure turns satisfying that requirement into a matter of keeping the
            right year folders, rather than reconstructing a record after the fact.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The hard part: documents that arrive piecemeal
          </h2>

          <p>
            The structure above is easy to draw and miserable to maintain by hand, because
            documents do not arrive sorted. They trickle in over weeks, named nothing useful,
            from clients who upload a brokerage statement and a charity receipt in the same
            email. Manually renaming and filing each one is the work that eats a season.
          </p>

          <p>
            Reading the document content is what makes this automatic. Sortio opens each file,
            recognizes a W-2 from a 1099 from a bank statement by what is printed on it, extracts
            the client name and the payer or account, builds the filename from your template, and
            drops it into the right client and year folder. Here is a prompt that does it:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each tax document and file it under
Clients/{Client}/{TaxYear}/{Category}/.
- Client is the taxpayer or business the document belongs to.
- TaxYear is the tax year printed on the form, not today's date.
- Category is Income (W-2, 1099, K-1), Deductions (receipts,
  donations), Statements (bank, brokerage), or Returns.
Rename to {TaxYear}_{Client}_{DocType}_{Detail}, where DocType is the
form name (1099-NEC, W-2, Bank-Statement) and Detail is the payer,
account last 4, or vendor if present.`}
          </pre>

          <p>
            Run it in preview first so you can validate the client and year routing on a real
            batch before applying it to the whole intake folder.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Keeping it confidential
          </h2>

          <p>
            Client tax data is exactly the kind of content firms are right to be careful with.
            Sortio can run entirely on the machine via local inference (Ollama), so returns and
            financial statements are read and sorted without ever leaving the device. Firms that
            want managed AI for speed can use the hosted option or bring their own key. The full
            tradeoff is in our piece on{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI for file organization
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/for-accountants"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Sortio for accountants and bookkeepers
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/make-client-files-audit-ready"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to make client files audit-ready
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Clear the intake pile before next season</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a folder of unsorted
              client documents, run Preview, and see them filed by client, year, and category
              before you apply. Sortio for Teams adds shared rules and an admin console at $29
              per seat for the whole practice.
            </p>
            <Link
              to="/for-accountants"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Sortio for accountants
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizeClientTaxDocumentsBlogPage;
